import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { SCOPE_GROUPS_QUERY_SEPARATOR } from '@this+that/constants';
import {
  GOOGLE_SCOPE_GROUPS,
  GoogleIntegrationStatus,
  GoogleScopeGroup,
} from '@this+that/models';
import log from 'loglevel';

import { pushGTMEvent } from '../utils/analytics/gtm';
import { createApiWithAuth } from '../utils/axios';

let googleApi: ReturnType<typeof createApiWithAuth> | null = null;

const getGoogleApi = () => {
  if (!googleApi) {
    const baseUrl = import.meta.env.VITE_GOOGLE_BASE_URL;
    if (!baseUrl) {
      throw new Error('Google base URL is not set');
    }
    googleApi = createApiWithAuth(baseUrl);
  }
  return googleApi;
};

// The endpoint requires an explicit set — it has no default now that Gmail is not implied
// by every Google connection. The default lives HERE, where the caller's intent is known.
//
// The mailbox alone: every caller that knows its surface names it, so the only thing left
// on this default is the assistant's provider-level card answering a generic "how do I
// connect Google?". Consenting to the calendar is that card's to ask for separately — a
// prompt for a surface the user never mentioned is the thing being removed here.
export const getGoogleIntegrationUrl = async (
  isOnboarding: boolean = false,
  scopeGroups: GoogleScopeGroup[] = [GOOGLE_SCOPE_GROUPS.GMAIL],
) => {
  try {
    const { data } = await getGoogleApi().get('/oauth-url', {
      params: {
        isOnboarding,
        scopeGroups: scopeGroups.join(SCOPE_GROUPS_QUERY_SEPARATOR),
      },
    });
    return data;
  } catch (error) {
    log.error('Error fetching authentication data:', error);
    throw error;
  }
};

export const disconnectGoogleIntegration = async (
  gmailAddress: string,
  deleteData: boolean,
  // The ONE surface the card being clicked represents. Required: it is what keeps that
  // surface out of the restore offer, and a caller that omits it would silently offer back
  // the thing the user just removed.
  scopeGroup: GoogleScopeGroup,
) => {
  try {
    await getGoogleApi().post('/unsubscribe', {
      gmailAddress,
      deleteData,
      scopeGroup,
    });
    pushGTMEvent({
      event: 'integration_disconnected',
    });
  } catch (error) {
    log.error('Error unsubscribing google:', error);
    throw error;
  }
};

export const scheduleGoogleInitialSync = async (
  code: string,
  state: string | null,
  isOnboarding: boolean = false,
) => {
  try {
    const { data } = await getGoogleApi().post('/initial-sync-scheduler', {
      code,
      state,
      isOnboarding,
    });
    pushGTMEvent({
      event: 'integration_connected',
    });

    return data;
  } catch (error) {
    log.error('Error scheduling initial synchronization:', error);
    throw error;
  }
};

const GET_USER_GOOGLE_INTEGRATION_STATUS = gql`
  query getUserGoogleIntegrationStatus {
    getUserGoogleIntegrationStatus {
      userId
      gmailAddress
      gmailEnabled
      calendarEnabled
      chatEnabled
      driveEnabled
      businessProfileEnabled
      gbpSelectedLocationNames
    }
  }
`;

interface GetUserGoogleIntegrationStatusData {
  getUserGoogleIntegrationStatus: GoogleIntegrationStatus[];
}

export function useGetUserGoogleIntegrationStatus() {
  const { loading, refetch } = useQuery<GetUserGoogleIntegrationStatusData>(
    GET_USER_GOOGLE_INTEGRATION_STATUS,
    {
      skip: true,
    },
  );

  const getUserGoogleIntegrationStatus = useCallback(async (): Promise<
    GoogleIntegrationStatus[] | null
  > => {
    try {
      const result = await refetch();

      if (result.error || !result.data) {
        log.error('Error fetching Google integration status:', result.error);
        return null;
      }

      return result.data.getUserGoogleIntegrationStatus;
    } catch (error) {
      log.error('Error fetching Google integration status:', error);
      return null;
    }
  }, [refetch]);

  return { getUserGoogleIntegrationStatus, loading };
}

export interface GoogleRestoreSnapshot {
  gmailAddress: string;
  scopeGroups: GoogleScopeGroup[];
  capturedAt: number;
}

const GET_GOOGLE_RESTORE_SNAPSHOTS = gql`
  query getGoogleRestoreSnapshots {
    getGoogleRestoreSnapshots {
      gmailAddress
      scopeGroups
      capturedAt
    }
  }
`;

interface GetGoogleRestoreSnapshotsData {
  getGoogleRestoreSnapshots: GoogleRestoreSnapshot[];
}

/**
 * What the user can offer to reconnect after a disconnect.
 *
 * The offer is server state, not an event: nothing pushes "your revoke finished" to the
 * browser, and nothing needs to. Reading it on mount is what makes the banner survive
 * closing the tab and the OAuth round trip itself.
 */
export function useGetGoogleRestoreSnapshots() {
  const { loading, refetch } = useQuery<GetGoogleRestoreSnapshotsData>(
    GET_GOOGLE_RESTORE_SNAPSHOTS,
    { skip: true },
  );

  const getGoogleRestoreSnapshots = useCallback(async () => {
    try {
      const { data } = await refetch();

      return data?.getGoogleRestoreSnapshots ?? [];
    } catch (error) {
      log.error('Error fetching Google restore snapshots:', error);
      return [] as GoogleRestoreSnapshot[];
    }
  }, [refetch]);

  return { getGoogleRestoreSnapshots, loading };
}

export interface GbpLocation {
  name: string;
  title?: string | null;
  accountName: string;
}

const GET_GBP_LOCATIONS = gql`
  query getGbpLocations($gmailAddress: String!) {
    getGbpLocations(gmailAddress: $gmailAddress) {
      name
      title
      accountName
    }
  }
`;

interface GetGbpLocationsData {
  getGbpLocations: GbpLocation[];
}

export function useGetGbpLocations() {
  const [runQuery, { loading }] = useLazyQuery<GetGbpLocationsData>(
    GET_GBP_LOCATIONS,
    {
      fetchPolicy: 'network-only',
    },
  );

  const getGbpLocations = useCallback(
    async (gmailAddress: string) => {
      const { data, error } = await runQuery({ variables: { gmailAddress } });
      if (error) {
        throw error;
      }
      return data?.getGbpLocations ?? [];
    },
    [runQuery],
  );

  return { getGbpLocations, loading };
}

const SET_GBP_SELECTED_LOCATIONS_MUTATION = gql`
  mutation setGbpSelectedLocations(
    $gmailAddress: String!
    $locationNames: [String!]!
  ) {
    setGbpSelectedLocations(
      gmailAddress: $gmailAddress
      locationNames: $locationNames
    )
  }
`;

interface SetGbpSelectedLocationsData {
  setGbpSelectedLocations: boolean;
}

export function useSetGbpSelectedLocations() {
  const [setGbpSelectedLocationsMutation, { loading }] =
    useMutation<SetGbpSelectedLocationsData>(
      SET_GBP_SELECTED_LOCATIONS_MUTATION,
    );

  const setGbpSelectedLocations = useCallback(
    async (gmailAddress: string, locationNames: string[]) => {
      const { data } = await setGbpSelectedLocationsMutation({
        variables: { gmailAddress, locationNames },
      });
      return data?.setGbpSelectedLocations ?? false;
    },
    [setGbpSelectedLocationsMutation],
  );

  return { setGbpSelectedLocations, loading };
}
